import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Card,
  Grid,
  Box,
  ClickAwayListener,
  Menu,
  MenuItem,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ScienceIcon from "@mui/icons-material/Science";
import { useSelector } from "react-redux";
import { RootState, useDispatch } from "../../../store";
import useAuth from "../../../hooks/useAuth";
import { getLoggedInUserDetail, getCompanyName } from "../../../store/slices/user";
import ThemeToggle from "../../ThemeToggleButton"; // Import ThemeToggle
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
// import logo from "../../../logo/logo.png";
import logo from "../../../logo/green_pulse_logo.png";
import { getCompanyId } from "../../../utlils/storageUtils";

type MenuItemType = {
  id: string;
  icon: React.ReactNode;
  label: string;
  heading: string;
  description: string;
  path: string | string[];
};

const menuItems: MenuItemType[] = [
  {
    id: "parameterLabelConfiguration",
    icon: <TuneIcon fontSize="medium" />,
    label: "Label Configuration",
    heading: "Label Configuration",
    description: "Label Configuration",
    path: "/parameterLabelConfiguration",
  },
  // {
  //   id: "setup",
  //   icon: <SettingsIcon fontSize="medium" />,
  //   label: "Setup",
  //   heading: "Setup",
  //   description: "Setup.",
  //   path: "/setup",
  // },
  {
    id: "dashboard",
    icon: <DashboardIcon fontSize="medium" />,
    label: "Dashboard",
    heading: "Dashboard",
    description: "Get a quick summary of your data and performance.",
    path: "/dashboard",
  },
  {
    id: "users",
    icon: <PeopleIcon fontSize="medium" />,
    label: "Users",
    heading: "User Management",
    description: "View, add, and manage system users.",
    path: "/user",
  },
  {
    id: "company-info",
    icon: <BusinessIcon fontSize="medium" />,
    label: "Company Info",
    heading: "Company Information",
    description: "View and update your company profile and details.",
    path: "/companyInfo",
  },
  {
    id: "company",
    icon: <ApartmentIcon fontSize="medium" />,
    label: "Company",
    heading: "Company ",
    description: "Manage company, admins, and license files.",
    path: "/companies",
  },
  {
    id: "data-entry",
    icon: <NoteAltIcon fontSize="medium" />,
    label: "Data Entry",
    heading: "Data Entry Section",
    description: "Input and manage business-related data records.",
    path: ["/dataList", "/fieldScreen", "/recommendation"],
  },
  {
    id: "simulation",
    icon: <ScienceIcon fontSize="medium" />,
    label: "Simulation",
    heading: "Simulation",
    description: "Create and manage simulation workflows.",
    path: ["/simulation/models", "/simulation"],
  },
];

const Topbar: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch()
  const mode = useSelector((state: RootState) => state.themeState.mode);
  const { logout, isAdmin } = useAuth();
  const { loggedInUserDetail, companyName } = useSelector(
    (state: RootState) => state.userState
  );

  const companyInfo = useSelector((state: RootState) => state.componyInfoState.companyInfoDetials)
  // Get the raw data from localStorage
  const getUserId = localStorage.getItem("userId");

  const companyId = getCompanyId();

  useEffect(() => {
    if (getUserId) {
      dispatch(getLoggedInUserDetail(String(getUserId)));
    }
  }, [])

  useEffect(() => {
    dispatch(getCompanyName());
  }, [])

  // filter menu items based on isAdmin and super admin
  const filteredMenuItems = companyId === 1
    ? menuItems.filter((item) => ["company"].includes(item.id))
    :
    isAdmin
      // Admins can see all app sections except company-only setup screens.
      ? menuItems.filter((item) => !["company"].includes(item.id))
      // Non-admin users get the core pages plus the new simulation menu.
      : menuItems.filter((item) => ["dashboard", "data-entry", "simulation"].includes(item.id));

  // Derive current menu item from path
  const currentItem = filteredMenuItems?.find((item) => {
    if (Array.isArray(item.path)) return item.path.some((p) => location.pathname === p || location.pathname.startsWith(p));
    return location.pathname === item.path || location.pathname.startsWith(item.path);
  })

  // Update tab title whenever path changes
  useEffect(() => {
    if (currentItem) {
      document.title = currentItem ? currentItem.heading : "Green Pulse-Client Portal";
    }
  }, [currentItem]);

  const handleMenuClick = () => setDropdownOpen((prev) => !prev);
  const handleClickAway = () => setDropdownOpen(false);

  // account menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = () => {
    navigate("/changePassword")
  }

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: 60,
          justifyContent: "center",
          // 3f7ff8ff
          backgroundColor: mode === "light" ? "#E5E5E5" : "#E5E5E5",
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "60px !important",
            justifyContent: "space-between",
            display: "flex",
          }}
        >
          {/* LEFT SECTION */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              onClick={handleMenuClick}
              ref={menuButtonRef}
              sx={{ mr: 2, "& svg": { color: mode === "light" ? "black" : "black" } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                color: mode === "light" ? "black" : "black",
                fontSize: 25,
                fontWeight: 600,
              }}
            >
              {companyName}
            </Typography>
          </Box>

          {/* RIGHT SECTION */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                fontWeight: 500,
                color: mode === "light" ? "black" : "black",
                mr: 1,
              }}
            >
              {loggedInUserDetail?.firstName} {loggedInUserDetail?.lastName}
            </Typography>


            <IconButton
              size="large"
              edge="end"
              onClick={handleMenu}
              sx={{
                "& svg": {
                  color: mode === "light" ? "black" : "black", marginRight: 4 // <--- adds space after the Profile icon
                }
              }}
            >
              <AccountCircle />
            </IconButton>
            {/* Company Logo */}
            <div
              style={{
                width: 90,
                height: 55,
                // backgroundColor: "white",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={logo}
                alt="logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  transform: "scale(3.5)",   // zoom inside
                  transformOrigin: "center",
                }}
              />
            </div>
          </Box>

          {/* MENU */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  minWidth: 200,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                  border: "5px solid lightgrey",
                  backgroundColor: mode === "light" ? "#fff" : "#111929",
                },
              },
            }}
          >
            <MenuItem onClick={handleClose}>
              Dark Mode <ThemeToggle />
            </MenuItem>
            <MenuItem onClick={handleChangePassword}>Change Password</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>

      </AppBar>

      {/* Dropdown Menu */}
      {dropdownOpen && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Card
            sx={{
              position: "absolute",
              top: 60,
              left: menuButtonRef.current
                ? menuButtonRef.current.getBoundingClientRect().left
                : 10,
              width: 210,
              p: 2,
              zIndex: 1201,
              borderRadius: 3,
              backgroundColor: mode === "light" ? "#fff" : "#111929",
              border: "5px solid lightgrey",
            }}
          >
            <Grid container direction="column" spacing={1}>
              {filteredMenuItems?.map((item) => {
                const isActive = Array.isArray(item.path)
                  ? item.path.some((p) => location.pathname.startsWith(p))
                  : location.pathname.startsWith(item.path);

                return (
                  <Grid
                    item
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      p: 1,
                      borderRadius: 2,
                      // backgroundColor: isActive
                      //   ? mode === "light"
                      //     ? "#73a3faff"
                      //     : "rgba(189, 235, 251, 0.3)"
                      //   : "transparent",
                      // "&:hover": {
                      //   backgroundColor: isActive
                      //     ? undefined
                      //     : mode === "light"
                      //       ? "rgba(0,0,0,0.05)"
                      //       : "rgba(255,255,255,0.05)",
                      // },
                      backgroundColor: isActive ? "#3f7ff8ff" : "transparent", // ✅ match AppBar
                      "&:hover": {
                        backgroundColor: isActive ? "#3f7ff8ff" : "rgba(0,0,0,0.05)", // keep hover consistent
                      },
                    }}
                    onClick={() => {
                      const path = Array.isArray(item.path)
                        ? item.path[0]
                        : item.path;
                      navigate(path);
                      setDropdownOpen(false);
                    }}
                  >
                    <Box
                      sx={{
                        height: 36,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                        // color: isActive ? "#00B4D8" : "inherit",
                        "& svg": {
                          color: isActive ? "#fff" : "inherit", // ✅ icon turns white if active
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 400, color: isActive ? "white" : "inherit",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </ClickAwayListener>
      )}
    </>
  );
};

export default Topbar;
